/**
 * Autosave gate for the caption editors.
 *
 * Stale-tab data-loss guard (issue #2347): a caption editor tab autosaves its
 * whole in-memory document every 120 seconds. Without a gate, a tab left open
 * on an old copy of the document will eventually PUT that old copy over newer
 * edits saved from another tab — including another tab of the *same* account,
 * which the concurrent-edit poller used to classify as a harmless "our own
 * save".
 *
 * A dirty flag alone is not enough to close this. Several caption mutation
 * paths historically updated the document without marking it dirty (Find and
 * Replace via `updateCaptionDocument`, undo/redo), so gating only on "dirty"
 * would have silently stopped autosaving real edits. The authoritative guard is
 * therefore the server precondition: never autosave unless the server copy is
 * still the one this tab's document is based on.
 *
 * ## What this does not do
 *
 * This is a client-side preflight, so it is a check followed by a separate,
 * unconditional write. Two tabs can both read the same `last_modified`, both
 * decide `save`, and both PUT — the second silently replacing the first, with
 * both tabs showing "synced". Reading first narrows the window from the full
 * 120-second autosave interval to the preflight round trip, which is a large
 * reduction but not a guarantee.
 *
 * Closing it properly needs the write itself to carry the expected version and
 * the server to reject a mismatch — an `If-Match` / `expected_last_modified`
 * precondition on the document PUT, returning 409 or 412 so the loser reloads
 * instead of overwriting. That is a backend change and is not implemented here;
 * until it exists, treat this gate as removing the systematic stale-tab
 * overwrite, not as making concurrent writes safe.
 */

export type AutosaveDecision =
  /** Local changes exist and the server has not moved past our baseline. */
  | 'save'
  /** Document has no segments — corruption, not an edit (Den Haag, 2026-06). */
  | 'skip-empty'
  /** Nothing changed locally, so there is nothing to push. */
  | 'skip-clean'
  /**
   * Worth saving, but the server's current state has not been read yet. The
   * caller should probe and ask again with the result; this keeps the ordering
   * of the checks in one tested place instead of splitting it across callers.
   */
  | 'needs-probe'
  /** Cannot establish what server state we are based on — refuse to guess. */
  | 'skip-unverified'
  /** The server advanced past our baseline: another tab or user wrote. */
  | 'conflict';

export interface AutosaveDecisionInput {
  /** Number of segments (cues) in the document about to be sent. */
  segmentCount: number;
  /**
   * Whether this tab holds changes the server has not seen. Callers should OR
   * the dirty flag with `saveStatus !== 'synced'`: the two have drifted apart
   * before, and either one being set is enough to mean "we have work to push".
   */
  hasLocalChanges: boolean;
  /**
   * Epoch ms of the server `last_modified` that this tab's document is based
   * on. Advanced only by this tab's own successful saves and by an explicit
   * reload from the server. 0 means unknown.
   */
  baselineLastModified: number;
  /**
   * Epoch ms of the server's current `last_modified`.
   *
   *  - `undefined` — not probed yet, so the answer is `needs-probe`.
   *  - `null` — probed and failed (offline, endpoint missing).
   *  - a number — the server's current value.
   */
  serverLastModified?: number | null;
  /**
   * Tolerance for server timestamp granularity. Both timestamps come from the
   * server, so this guards against second-resolution rounding, not clock skew.
   */
  graceMs?: number;
}

/**
 * Decide whether an autosave may write the in-memory document to the server.
 *
 * Checks run cheapest-first, and every non-`save` outcome is safe: the draft
 * stays in localStorage, so skipping an autosave never loses work — the next
 * interval retries, and an explicit user save (Ctrl+S) always bypasses this
 * gate. `conflict` is the caller's cue to surface the concurrent-edit banner so
 * the user can reload or deliberately keep their own version.
 *
 * Call it twice: once with `serverLastModified` left out, which answers
 * `needs-probe` when a probe is actually worth a request, and again with the
 * probe result. When the server state cannot be established (`null`, or no
 * baseline is known) the answer is `skip-unverified` rather than `save`. That
 * trades a delayed autosave for the guarantee that an unverified tab never
 * overwrites a body it has not compared against.
 */
export function resolveAutosave({
  segmentCount,
  hasLocalChanges,
  baselineLastModified,
  serverLastModified,
  graceMs = 1000
}: AutosaveDecisionInput): AutosaveDecision {
  // Never push an empty document: an empty editor state is the symptom of a
  // corrupted or stale draft, not a real edit, and saving it wipes the server
  // copy's segments.
  if (segmentCount <= 0) return 'skip-empty';

  // The stale clean tab from issue #2347. Nothing to push, so do not push.
  // Checked before the probe so a clean tab costs no requests at all.
  if (!hasLocalChanges) return 'skip-clean';

  // Worth saving — now the server state has to be read before we write.
  if (serverLastModified === undefined) return 'needs-probe';

  // We have real work, but no way to tell whether we are based on the current
  // server body. Hold the draft locally instead of gambling with a blind PUT.
  if (serverLastModified === null || baselineLastModified <= 0) {
    return 'skip-unverified';
  }

  // Somebody wrote after the state we loaded — another user, or this same
  // account in another tab. Either way our document no longer contains their
  // work, so pushing it would discard it.
  if (serverLastModified > baselineLastModified + graceMs) return 'conflict';

  return 'save';
}

/**
 * True when this tab holds changes the server has not seen.
 *
 * Both signals are consulted because they have diverged in practice: some
 * mutation paths set `saveStatus` without the dirty flag (undo/redo persist
 * `saved-locally`) and vice versa. Treating either as authoritative keeps real
 * edits autosaving while the stale *clean* tab still reads as clean.
 */
export function hasPendingLocalChanges(
  isDirty: boolean | undefined,
  saveStatus: 'unsaved' | 'saved-locally' | 'synced' | 'saving'
): boolean {
  return !!isDirty || saveStatus !== 'synced';
}
